import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight, Instagram, Star, Truck, RefreshCw, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import Countdown from "@/components/Countdown";

function Section({ eyebrow, title, cta, ctaTo, children }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 my-16 sm:my-24">
      <div className="flex items-end justify-between mb-8">
        <div>
          {eyebrow && <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FF2E4C] mb-2">{eyebrow}</p>}
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight">{title}</h2>
        </div>
        {cta && <Link to={ctaTo} className="hidden sm:flex items-center gap-1.5 font-display font-bold text-sm uppercase tracking-tight hover:text-[#FF2E4C] transition-colors group">{cta}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Link>}
      </div>
      {children}
      {cta && <Link to={ctaTo} className="sm:hidden mt-6 flex items-center justify-center gap-1.5 font-display font-bold text-sm uppercase tracking-tight border border-zinc-700 py-3 rounded">{cta}<ArrowRight className="w-4 h-4" /></Link>}
    </section>
  );
}

export default function Home() {
  const { settings } = useOutletContext();
  const [featured, setFeatured] = useState([]);
  const [newDrop, setNewDrop] = useState([]);
  const [best, setBest] = useState([]);
  const [limited, setLimited] = useState([]);

  useEffect(() => {
    api.get("/products?badge=featured").then(({ data }) => setFeatured(data.slice(0, 4))).catch(() => {});
    api.get("/products?badge=new").then(({ data }) => setNewDrop(data.slice(0, 4))).catch(() => {});
    api.get("/products?badge=best_seller").then(({ data }) => setBest(data.slice(0, 4))).catch(() => {});
    api.get("/products?badge=limited").then(({ data }) => setLimited(data.slice(0, 4))).catch(() => {});
  }, []);

  const hero = "https://images.unsplash.com/photo-1559697242-a465f2578a95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHx1cmJhbiUyMHN0cmVldHdlYXIlMjBtYWxlJTIwZmFzaGlvbiUyMGhvb2RpZSUyMGRhcmslMjBlZGl0b3JpYWx8ZW58MHx8fHwxNzg5NDc2Mzk5fDA&ixlib=rb-4.1.0&q=85";
  const igImages = [
    "https://images.unsplash.com/photo-1614028574724-baea020fbe9e?w=400&q=80",
    "https://images.unsplash.com/photo-1580079906050-84bb132e4c4f?w=400&q=80",
    "https://images.unsplash.com/photo-1565978771542-0db9ab9ad3de?w=400&q=80",
    "https://images.unsplash.com/photo-1594587002961-1a75dfaf34b8?w=400&q=80",
    "https://images.unsplash.com/photo-1633292750937-120a94f5c2bb?w=400&q=80",
    "https://images.unsplash.com/photo-1559697242-a465f2578a95?w=400&q=80",
  ];

  const reviews = [
    { name: "Γιώργος Π.", text: "Απίστευτη ποιότητα, το hoodie είναι heavyweight και η εκτύπωση τέλεια. Το καλύτερο streetwear brand στην Ελλάδα.", rating: 5 },
    { name: "Μαρία Κ.", text: "Γρήγορη αποστολή στη Θεσσαλονίκη και άψογα υλικά. Θα ξαναπαραγγείλω σίγουρα!", rating: 5 },
    { name: "Δημήτρης Α.", text: "Το fit είναι ακριβώς όπως στις φωτογραφίες. Premium αίσθηση σε καλή τιμή.", rating: 5 },
  ];

  return (
    <div>
      <Seo title={null} description={settings?.hero_subtitle || "Premium streetwear από τη Θεσσαλονίκη"} image={settings?.hero_image} />
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[560px] flex items-end overflow-hidden">
        <img src={settings?.hero_image || hero} alt="SKGWEAR" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full pb-16 sm:pb-24">
          <p className="font-mono text-[11px] sm:text-xs tracking-[0.35em] uppercase text-[#FF2E4C] mb-4 animate-fade-up">{settings?.hero_subtitle || "PREMIUM STREETWEAR · ΘΕΣΣΑΛΟΝΙΚΗ"}</p>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.9] text-balance animate-fade-up" style={{ animationDelay: "80ms" }}>
            CLOTHES FOR<br />A BETTER <span className="text-[#FF2E4C]">YOU</span>
          </h1>
          <div className="flex flex-col sm:flex-row gap-3 mt-8 animate-fade-up" style={{ animationDelay: "160ms" }}>
            <Link to="/shop" data-testid="hero-shop-now-cta" className="btn-red px-10 py-4 text-sm font-bold tracking-widest uppercase rounded inline-flex items-center justify-center gap-2 group">
              SHOP NOW <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/shop?badge=limited" className="px-10 py-4 text-sm font-bold tracking-widest uppercase rounded border border-white/30 glass hover:bg-white hover:text-black transition-colors inline-flex items-center justify-center">
              LIMITED EDITION
            </Link>
          </div>
        </div>
      </section>

      {/* USP BAR */}
      <div className="border-y border-zinc-800 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-zinc-800">
          {[[Truck, "ΔΩΡΕΑΝ ΜΕΤΑΦΟΡΙΚΑ", "Για παραγγελίες άνω των €80"], [RefreshCw, "ΕΥΚΟΛΕΣ ΕΠΙΣΤΡΟΦΕΣ", "14 ημέρες δωρεάν επιστροφή"], [ShieldCheck, "ΑΣΦΑΛΕΙΣ ΠΛΗΡΩΜΕΣ", "Stripe · Αντικαταβολή"]].map(([Icon, t, s], i) => (
            <div key={i} className="flex items-center gap-3 py-5 px-2">
              <Icon className="w-6 h-6 text-[#FF2E4C] shrink-0" />
              <div><p className="font-display font-bold text-sm uppercase tracking-tight">{t}</p><p className="text-xs text-zinc-500">{s}</p></div>
            </div>
          ))}
        </div>
      </div>

      {newDrop.length > 0 && <Section eyebrow="ΝΕΑ ΣΥΛΛΟΓΗ" title="NEW DROP" cta="ΔΕΣ ΟΛΑ" ctaTo="/shop?badge=new">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">{newDrop.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
      </Section>}

      {/* EDITORIAL BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 my-16 sm:my-24">
        <Link to={settings?.banner_link || "/shop?badge=limited"} className="relative block h-[400px] sm:h-[520px] overflow-hidden rounded-lg group">
          <img src={settings?.banner_image || "https://images.unsplash.com/photo-1594587002961-1a75dfaf34b8?w=1400&q=85"} alt="Limited" className="absolute inset-0 w-full h-full object-cover img-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B]/90 to-transparent" />
          <div className="relative h-full flex flex-col justify-center p-8 sm:p-16 max-w-lg">
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FF2E4C] mb-3">{settings?.banner_eyebrow || "ΠΕΡΙΟΡΙΣΜΕΝΗ ΕΚΔΟΣΗ"}</p>
            <h2 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter leading-none mb-4 whitespace-pre-line">{settings?.banner_title || "MEMENTO\nMORI"}</h2>
            <p className="text-zinc-300 mb-6 max-w-sm">{settings?.banner_subtitle || "Numbered drop. Μόνο 100 κομμάτια παγκοσμίως. Μην το χάσεις."}</p>
            {settings?.drop_end && <Countdown endDate={settings.drop_end} className="mb-6" />}
            <span className="inline-flex items-center gap-2 font-display font-bold text-sm uppercase tracking-tight group-hover:text-[#FF2E4C] transition-colors">ΑΓΟΡΑ ΤΩΡΑ <ArrowRight className="w-4 h-4" /></span>
          </div>
        </Link>
      </section>

      {best.length > 0 && <Section eyebrow="PUBLIC FAVORITES" title="BEST SELLERS" cta="ΔΕΣ ΟΛΑ" ctaTo="/shop?badge=best_seller">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">{best.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
      </Section>}

      {featured.length > 0 && <Section eyebrow="STREETWEAR ESSENTIALS" title="STREETWEAR" cta="ΔΕΣ ΟΛΑ" ctaTo="/shop">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">{featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
      </Section>}

      {limited.length > 0 && <Section eyebrow="ΠΕΡΙΟΡΙΣΜΕΝΗ ΕΚΔΟΣΗ" title="LIMITED EDITION" cta="ΔΕΣ ΟΛΑ" ctaTo="/shop?badge=limited">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">{limited.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
      </Section>}

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 my-16 sm:my-24">
        <div className="text-center mb-10">
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FF2E4C] mb-2">ΤΙ ΛΕΝΕ ΟΙ ΠΕΛΑΤΕΣ</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight">REVIEWS</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <div key={i} className="card-surface rounded-lg p-6">
              <div className="flex gap-0.5 mb-3">{Array.from({ length: r.rating }).map((_, k) => <Star key={k} className="w-4 h-4 fill-[#FF2E4C] text-[#FF2E4C]" />)}</div>
              <p className="text-zinc-300 text-sm leading-relaxed mb-4">"{r.text}"</p>
              <p className="font-display font-bold text-sm uppercase tracking-tight">{r.name} <span className="text-[#FF2E4C] font-mono text-[10px] ml-1">✓ VERIFIED</span></p>
            </div>
          ))}
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 my-16 sm:my-24">
        <div className="text-center mb-8">
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FF2E4C] mb-2">#SKGWEAR</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight">FOLLOW SKGWEAR</h2>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
          {igImages.map((src, i) => (
            <a key={i} href={settings?.instagram || "https://instagram.com/skgwear"} target="_blank" rel="noreferrer" className="relative aspect-square overflow-hidden rounded group">
              <img src={src} alt="ig" loading="lazy" className="w-full h-full object-cover img-zoom" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Instagram className="w-6 h-6 text-white" /></div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
